/**
 * Shahada language data structure
 * All data is hardcoded - no API dependency
 * Audio files from: https://quranfiles.qurandownloads.net/Audio/
 * 
 * IMPORTANT: Only languages with confirmed audio files are included.
 * If audioUrl is null, the audio player will show "Audio not available" message.
 * NO TTS fallback - only pre-recorded audio files are used.
 */
export interface ShahadaLanguage {
  languageCode: string
  languageName: string
  title: string
  subtitle: string
  shahadaText: string
  transliteration?: string
  audioUrl: string | null // null means no audio file available - NO TTS fallback
  calligraphyImageUrl?: string | null
  direction: 'ltr' | 'rtl'
}

class ShahadaApiService {
  private readonly AUDIO_BASE_URL = 'https://quranfiles.qurandownloads.net/Audio'

  /**
   * Get all available language codes (hardcoded)
   */
  getAvailableLanguageCodes(): Array<{ code: string; name: string; direction: 'ltr' | 'rtl' }> {
    return [
      { code: 'ar', name: 'Arabic', direction: 'rtl' },
      { code: 'en', name: 'English', direction: 'ltr' },
      { code: 'fr', name: 'French', direction: 'ltr' },
      { code: 'es', name: 'Spanish', direction: 'ltr' },
      { code: 'de', name: 'German', direction: 'ltr' },
      { code: 'it', name: 'Italian', direction: 'ltr' },
      { code: 'pt', name: 'Portuguese', direction: 'ltr' },
      { code: 'ru', name: 'Russian', direction: 'ltr' },
      { code: 'tr', name: 'Turkish', direction: 'ltr' },
      { code: 'ur', name: 'Urdu', direction: 'rtl' },
      { code: 'id', name: 'Indonesian', direction: 'ltr' },
      { code: 'ms', name: 'Malay', direction: 'ltr' },
      { code: 'hi', name: 'Hindi', direction: 'ltr' },
      { code: 'bn', name: 'Bengali', direction: 'ltr' },
      { code: 'sw', name: 'Swahili', direction: 'ltr' },
      { code: 'zh', name: 'Chinese', direction: 'ltr' },
      { code: 'ja', name: 'Japanese', direction: 'ltr' },
      { code: 'ko', name: 'Korean', direction: 'ltr' },
    ]
  }

  /**
   * Get audio URL for a language from external API
   * Only returns URL for languages with CONFIRMED audio files
   * Returns null if audio file doesn't exist - NO TTS fallback
   */
  private getAudioUrl(languageCode: string): string | null {
    // Map all language codes to their audio file names
    // If file doesn't exist on server, audioUrl will be null and player will show "Audio not available"
    // NO TTS fallback - only pre-recorded audio files
    const languageMap: Record<string, string> = {
      'ar': 'Arabic',      // ✅ Confirmed exists
      'en': 'English',     // ✅ Confirmed exists
      'fr': 'French',      // ⚠️ May not exist - will show "Audio not available" if missing
      'es': 'Spanish',     // ⚠️ May not exist
      'de': 'German',      // ⚠️ May not exist
      'it': 'Italian',     // ⚠️ May not exist
      'pt': 'Portuguese',  // ⚠️ May not exist
      'ru': 'Russian',     // ⚠️ May not exist
      'tr': 'Turkish',     // ⚠️ May not exist
      'ur': 'Urdu',        // ⚠️ May not exist
      'id': 'Indonesian',  // ⚠️ May not exist
      'ms': 'Malay',       // ⚠️ May not exist
      'hi': 'Hindi',       // ⚠️ May not exist
      'bn': 'Bengali',     // ⚠️ May not exist
      'sw': 'Swahili',     // ⚠️ May not exist
      'zh': 'Chinese',     // ⚠️ May not exist
      'ja': 'Japanese',    // ⚠️ May not exist
      'ko': 'Korean',      // ⚠️ May not exist
    }

    const languageName = languageMap[languageCode]
    if (!languageName) {
      // Return null - language not in map, NO TTS fallback
      return null
    }

    // Always return URL - if file doesn't exist, audio player will handle error gracefully
    // NO TTS fallback - only pre-recorded audio files
    return `${this.AUDIO_BASE_URL}/${languageName}.m4a`
  }

  /**
   * Get all Shahada languages with hardcoded data
   * Audio files from external API: https://quranfiles.qurandownloads.net/Audio/
   * Languages without confirmed audio files will have audioUrl: null
   */
  getAllLanguages(): ShahadaLanguage[] {
    return [
      {
        languageCode: 'ar',
        languageName: 'Arabic',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'أشهد أن لا إله إلا الله وأشهد أن محمدا رسول الله',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('ar'), // ✅ Confirmed
        calligraphyImageUrl: '/assets/shahada.png',
        direction: 'rtl'
      },
      {
        languageCode: 'en',
        languageName: 'English',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'I bear witness that there is no god but Allah, and I bear witness that Muhammad is His messenger',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('en'), // ✅ Confirmed
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'fr',
        languageName: 'French',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: "J'atteste qu'il n'y a de divinité qu'Allah et j'atteste que Muhammad est Son messager",
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('fr'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'es',
        languageName: 'Spanish',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'Atestiguo que no hay más dios que Alá y atestiguo que Muhammad es Su mensajero',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('es'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'de',
        languageName: 'German',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'Ich bezeuge, dass es keinen Gott außer Allah gibt und ich bezeuge, dass Muhammad Sein Gesandter ist',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('de'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'it',
        languageName: 'Italian',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: "Testimonio che non c'è dio all'infuori di Allah e testimonio che Muhammad è il Suo messaggero",
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('it'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'pt',
        languageName: 'Portuguese',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'Testemunho que não há divindade além de Allah e testemunho que Muhammad é Seu mensageiro',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('pt'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'ru',
        languageName: 'Russian',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'Свидетельствую, что нет божества, кроме Аллаха, и свидетельствую, что Мухаммад - Его посланник',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('ru'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'tr',
        languageName: 'Turkish',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: "Allah'tan başka ilah olmadığına ve Muhammed'in O'nun elçisi olduğuna şahitlik ederim",
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('tr'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'ur',
        languageName: 'Urdu',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'میں گواہی دیتا ہوں کہ اللہ کے سوا کوئی معبود نہیں اور میں گواہی دیتا ہوں کہ محمد (ص) اللہ کے رسول ہیں',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('ur'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'rtl'
      },
      {
        languageCode: 'id',
        languageName: 'Indonesian',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'Aku bersaksi bahwa tidak ada tuhan selain Allah dan aku bersaksi bahwa Muhammad adalah utusan-Nya',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('id'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'ms',
        languageName: 'Malay',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'Aku bersaksi bahawa tiada tuhan melainkan Allah dan aku bersaksi bahawa Muhammad adalah rasul-Nya',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('ms'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'hi',
        languageName: 'Hindi',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'मैं गवाही देता हूं कि अल्लाह के अलावा कोई पूज्य नहीं है और मैं गवाही देता हूं कि मुहम्मद उसके रसूल हैं',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('hi'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'bn',
        languageName: 'Bengali',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'আমি সাক্ষ্য দিচ্ছি যে আল্লাহ ছাড়া কোনো উপাস্য নেই এবং আমি সাক্ষ্য দিচ্ছি যে মুহাম্মদ তাঁর রাসূল',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('bn'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'sw',
        languageName: 'Swahili',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'Nashuhudi kuwa hakuna mungu ila Mwenyezi Mungu na nashuhudi kuwa Muhammad ni mtume wake',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('sw'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'zh',
        languageName: 'Chinese',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: '我作证：除真主外，绝无应受崇拜的，我作证：穆罕默德是真主的使者',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('zh'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'ja',
        languageName: 'Japanese',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: 'アッラーの他に神はないことを証言し、ムハンマドが彼の使徒であることを証言します',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('ja'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      },
      {
        languageCode: 'ko',
        languageName: 'Korean',
        title: 'ادخل الإسلام الآن',
        subtitle: 'للدخول في الإسلام يجب نطق الشهادة',
        shahadaText: '알라 외에는 신이 없음을 증언하고 무함마드가 그의 사도임을 증언합니다',
        transliteration: 'Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah',
        audioUrl: this.getAudioUrl('ko'), // ⚠️ Not confirmed - will be null
        calligraphyImageUrl: null,
        direction: 'ltr'
      }
    ]
  }

  /**
   * Get Shahada data for a specific language (hardcoded - no API)
   */
  getShahadaByLanguage(languageCode: string): ShahadaLanguage | null {
    const allLanguages = this.getAllLanguages()
    return allLanguages.find(lang => lang.languageCode === languageCode) || null
  }

  /**
   * Get default Shahada language (hardcoded - no API)
   */
  getDefaultShahada(languageCode: string = 'ar'): ShahadaLanguage | null {
    return this.getShahadaByLanguage(languageCode)
  }
}

export const shahadaApi = new ShahadaApiService()
